import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Chess } from 'chess.js';
import { PIECE_IMAGES } from '../../utils/AppConstants';

const MiniPreviewBoard = React.memo(({ fen }) => {
    const temp = new Chess(); 
    try { 
        temp.load(fen); 
    } catch(e) {
        try { temp.load('rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'); } catch(err){}
    }
    const rows = [8,7,6,5,4,3,2,1]; 
    const cols = ['a','b','c','d','e','f','g','h'];
    const render = {}; 
    temp.board().forEach((row, r) => row.forEach((sq, c) => { 
        if (sq) render[`${cols[c]}${rows[r]}`] = sq.color + sq.type.toUpperCase(); 
    }));
    return (
        <div style={{ 
            width: '200px', 
            height: '200px', 
            display: 'grid', 
            gridTemplateColumns: 'repeat(8, 1fr)', 
            gridTemplateRows: 'repeat(8, 1fr)', 
            border: '3px solid rgba(255, 255, 255, 0.75)', 
            borderRadius: '10px', 
            overflow: 'hidden', 
            boxShadow: 'inset 3px 3px 6px rgba(163, 177, 198, 0.35), inset -3px -3px 6px rgba(255, 255, 255, 0.75)', 
            margin: '10px 0' 
        }}>
            {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                const id = `${col}${row}`; 
                const isDark = (rIdx + cIdx) % 2 === 1;
                return ( 
                    <div key={id} style={{ background: isDark ? '#9ca3af' : '#e5e7eb', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}> 
                        {render[id] && <img src={PIECE_IMAGES[render[id]]} alt="" style={{ width: '90%', height: '90%' }} />} 
                    </div> 
                );
            }))}
        </div>
    );
});

export default function AcademyHistory({ allClasses, allUsers, myEmail, isOwner }) {
    const [expandedClass, setExpandedClass] = useState(null);
    const [expandedTimelines, setExpandedTimelines] = useState({});
    const [visibleBoards, setVisibleBoards] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [coachFilter, setCoachFilter] = useState('all');
    const [showMyClassesOnly, setShowMyClassesOnly] = useState(false);

    const usersMap = useMemo(() => {
        const map = {};
        allUsers.forEach(u => {
            map[(u.email || '').toLowerCase()] = u.name || u.email?.split('@')[0];
        });
        return map;
    }, [allUsers]);

    const completedClasses = useMemo(() => {
        return allClasses
            .filter(c => c.status === 'COMPLETED')
            .sort((a, b) => (b.endedAt || 0) - (a.endedAt || 0));
    }, [allClasses]);

    // Gather list of unique coaches who have taught classes
    const uniqueCoaches = useMemo(() => {
        const coachesSet = new Set();
        completedClasses.forEach(c => {
            if (c.teacherId) coachesSet.add(c.teacherId.toLowerCase());
        });
        return Array.from(coachesSet);
    }, [completedClasses]);

    const filteredClasses = useMemo(() => {
        return completedClasses.filter(cls => {
            // My classes filter
            if (showMyClassesOnly && myEmail && cls.teacherId?.toLowerCase() !== myEmail.toLowerCase()) {
                return false;
            }

            // Coach selector filter
            if (coachFilter !== 'all' && cls.teacherId?.toLowerCase() !== coachFilter.toLowerCase()) {
                return false;
            }

            // Search query filter
            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase();
            const topicMatch = cls.topic?.toLowerCase().includes(q);
            const noteMatch = cls.coachStickyNote?.toLowerCase().includes(q);
            const coachName = usersMap[cls.teacherId?.toLowerCase()] || cls.teacherId || '';
            const coachMatch = coachName.toLowerCase().includes(q);

            return topicMatch || noteMatch || coachMatch;
        });
    }, [completedClasses, showMyClassesOnly, myEmail, coachFilter, searchQuery, usersMap]);

    const toggleTimeline = (classId, e) => {
        if (e) e.stopPropagation();
        setExpandedTimelines(prev => ({ ...prev, [classId]: !prev[classId] }));
    };

    const toggleBoard = (key, e) => {
        if (e) e.stopPropagation();
        setVisibleBoards(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const renderAttendance = (cls) => {
        const total = cls.students || [];
        const presenceData = cls.presence || {};
        const joined = total.filter(email => presenceData[email.replace(/\./g, '_')] === true);
        const absent = total.filter(email => presenceData[email.replace(/\./g, '_')] !== true);

        return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px' }}>
                <div style={{ background: '#e0e5ec', padding: '15px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.45)', boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.25), inset -2px -2px 5px rgba(255,255,255,0.7)' }}>
                    <div style={{ color: '#10b981', fontWeight: 'bold', marginBottom: '8px', fontSize: '12px', letterSpacing: '0.5px' }}>✅ JOINED ({joined.length})</div>
                    <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                        {joined.length === 0 ? (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic' }}>None</span>
                        ) : (
                            joined.map(e => {
                                const safeEmail = e.replace(/\./g, '_');
                                const remark = cls.studentClassRemarks?.[safeEmail];
                                return (
                                    <div key={e} style={{ display: 'flex', flexDirection: 'column', gap: '3px', background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.6)', padding: '5px 10px', borderRadius: '10px' }}>
                                        <span style={{ color: 'var(--text-primary)', fontSize: '12.5px', fontWeight: 'bold' }}>👤 {usersMap[e.toLowerCase()] || e.split('@')[0]}</span>
                                        {remark && <span style={{ color: '#be185d', fontSize: '11px', fontStyle: 'italic', fontWeight: '600', paddingLeft: '10px' }}>💬 "{remark}"</span>}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                <div style={{ background: '#e0e5ec', padding: '15px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.45)', boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.25), inset -2px -2px 5px rgba(255,255,255,0.7)' }}>
                    <div style={{ color: '#ef4444', fontWeight: 'bold', marginBottom: '8px', fontSize: '12px', letterSpacing: '0.5px' }}>❌ ABSENT ({absent.length})</div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {absent.length === 0 ? (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic' }}>None</span>
                        ) : (
                            absent.map(e => (
                                <span key={e} style={{ background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '1.5px 1.5px 4px rgba(163,177,198,0.2), -1.5px -1.5px 4px rgba(255,255,255,0.85)', padding: '4px 10px', borderRadius: '15px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                    👤 {usersMap[e.toLowerCase()] || e.split('@')[0]}
                                </span>
                            ))
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderTimeline = (cls, classIdx) => {
        const logs = cls.activityLog || [];
        const isTimelineOpen = !!expandedTimelines[cls.id];
        
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button 
                    onClick={(e) => toggleTimeline(cls.id, e)}
                    style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: '10px 14px', 
                        background: '#e0e5ec', 
                        border: '1px solid rgba(255,255,255,0.65)', 
                        color: 'var(--text-primary)', 
                        borderRadius: '10px', 
                        fontWeight: 'bold', 
                        cursor: 'pointer', 
                        fontSize: '12px', 
                        boxShadow: isTimelineOpen ? 'var(--neu-shadow-inset)' : '2px 2px 6px rgba(163,177,198,0.25), -2px -2px 6px rgba(255,255,255,0.7)',
                        transition: 'all 0.2s'
                    }}
                >
                    <span>⏳ Click to view Class Activity Log ({logs.length} events)</span>
                    <span>{isTimelineOpen ? '▲ Hide' : '▼ Expand'}</span>
                </button>

                {isTimelineOpen && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', position: 'relative', paddingLeft: '15px', marginTop: '10px', animation: 'fadeIn 0.25s ease-out' }}>
                        {logs.length === 0 ? (
                            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', fontStyle: 'italic' }}>No timeline events recorded.</div>
                        ) : (
                            <>
                                <div style={{ position: 'absolute', top: '8px', bottom: '8px', left: '24px', width: '2px', background: 'rgba(163, 177, 198, 0.3)' }}></div>
                                {logs.map((log, idx) => {
                                    const boardKey = `timeline-academy-${classIdx}-${idx}`;
                                    return (
                                        <div key={idx} style={{ display: 'flex', gap: '15px', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                                            <div style={{ 
                                                width: '18px', 
                                                height: '18px', 
                                                borderRadius: '50%', 
                                                background: '#3b82f6', 
                                                border: `2px solid var(--bg-primary)`, 
                                                display: 'flex', 
                                                justifyContent: 'center', 
                                                alignItems: 'center',
                                                flexShrink: 0
                                            }} />
                                            
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                                    <span style={{ color: 'var(--text-primary)', fontSize: '13.5px', fontWeight: '600' }}>{log.title}</span>
                                                    <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                                                        {new Date(log.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                </div>
                                                {log.fen && (
                                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '6px' }}>
                                                        <button 
                                                            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(log.fen); toast.success('FEN Copied!'); }}
                                                            style={{ background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '1.5px 1.5px 4px rgba(163,177,198,0.2), -1.5px -1.5px 4px rgba(255,255,255,0.85)', borderRadius: '6px', color: '#10b981', fontSize: '11px', padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'bold' }}
                                                        >
                                                            📋 Copy FEN
                                                        </button>
                                                        <button 
                                                            onClick={(e) => toggleBoard(boardKey, e)}
                                                            style={{ background: 'var(--bg-primary)', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '1.5px 1.5px 4px rgba(163,177,198,0.2), -1.5px -1.5px 4px rgba(255,255,255,0.85)', borderRadius: '6px', color: '#3b82f6', fontSize: '11px', padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'bold' }}
                                                        >
                                                            👁️ {visibleBoards[boardKey] ? 'Hide Board' : 'Preview Board'}
                                                        </button>
                                                    </div>
                                                )}
                                                {log.fen && visibleBoards[boardKey] && (
                                                    <div style={{ animation: 'fadeIn 0.2s ease-out' }}>
                                                        <MiniPreviewBoard fen={log.fen} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{ background: 'var(--bg-primary)', padding: '30px', borderRadius: '20px', boxShadow: 'var(--panel-shadow)', border: '1px solid rgba(255,255,255,0.55)', width: '100%' }}>
            
            {/* Header section */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                    <h2 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '22px', fontWeight: '800' }}>🏫 Academy Class Archives</h2>
                    <p style={{ color: 'var(--text-secondary)', margin: '4px 0 0 0', fontSize: '13px', fontWeight: '500' }}>Browse and inspect all past classroom sessions, lesson diaries, and board files.</p>
                </div>
            </div>

            {/* Filter controls */}
            <div style={{ 
                display: 'flex', 
                gap: '15px', 
                marginBottom: '30px', 
                flexWrap: 'wrap', 
                alignItems: 'center', 
                background: '#e0e5ec', 
                padding: '15px 20px', 
                borderRadius: '16px', 
                boxShadow: 'var(--neu-shadow-inset)',
                border: '1px solid rgba(255, 255, 255, 0.45)'
            }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-secondary)' }}>Filter Coach:</span>
                    <select 
                        value={coachFilter} 
                        onChange={(e) => setCoachFilter(e.target.value)} 
                        style={{ padding: '8px 12px', background: '#e0e5ec', border: '1px solid rgba(255, 255, 255, 0.45)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', cursor: 'pointer', boxShadow: 'var(--neu-shadow)', fontWeight: 'bold' }}
                    >
                        <option value="all">👨‍🏫 All Coaches</option>
                        {uniqueCoaches.map(email => (
                            <option key={email} value={email}>👨‍🏫 {usersMap[email] || email.split('@')[0]}</option>
                        ))}
                    </select>
                </div>

                {!isOwner && myEmail && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                        <input 
                            type="checkbox" 
                            checked={showMyClassesOnly} 
                            onChange={(e) => setShowMyClassesOnly(e.target.checked)}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        Show Only Classes Taught by Me
                    </label>
                )}

                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                    <input 
                        type="text" 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search class topics or coach name..."
                        style={{ padding: '8px 12px', paddingLeft: '30px', background: '#e0e5ec', border: '1px solid rgba(255, 255, 255, 0.45)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', width: '240px', outline: 'none', boxShadow: 'var(--neu-shadow)' }}
                    />
                    <span style={{ position: 'absolute', left: '10px', color: 'var(--text-secondary)', fontSize: '13px', pointerEvents: 'none' }}>🔍</span>
                </div>
            </div>

            {/* List of completed classes */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {filteredClasses.length === 0 ? (
                    <div style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '40px', background: '#e0e5ec', borderRadius: '12px', boxShadow: 'var(--neu-shadow-inset)', border: '1px solid rgba(255,255,255,0.45)' }}>
                        📭 No past classes matches your search criteria.
                    </div>
                ) : (
                    filteredClasses.map((cls, i) => {
                        const isExpanded = expandedClass === cls.id;
                        const attendanceCount = Object.values(cls.presence || {}).filter(v => v === true).length;
                        const taughtByMe = myEmail && cls.teacherId?.toLowerCase() === myEmail.toLowerCase();
                        
                        return (
                            <div key={cls.id || i} style={{ 
                                background: '#e0e5ec', 
                                borderRadius: '16px', 
                                border: '1px solid rgba(255,255,255,0.55)', 
                                boxShadow: isExpanded ? 'var(--panel-shadow-hover)' : 'var(--panel-shadow)',
                                overflow: 'hidden', 
                                transition: 'all 0.25s ease-in-out' 
                            }}>
                                {/* Header Row - Refactored to eliminate the flat AI pink stripe */}
                                <div 
                                    onClick={() => setExpandedClass(isExpanded ? null : cls.id)} 
                                    style={{ 
                                        padding: '18px 24px', 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center', 
                                        cursor: 'pointer',
                                        background: isExpanded ? 'var(--btn-hover)' : 'transparent',
                                        userSelect: 'none'
                                    }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <div style={{ fontSize: '16px', color: 'var(--text-primary)', fontWeight: '750', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span>{cls.topic}</span>
                                            {taughtByMe && (
                                                <span style={{ fontSize: '10px', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold' }}>Taught by You</span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                                            📅 {new Date(cls.endedAt || cls.scheduledAt).toLocaleString()} • Coach: {usersMap[cls.teacherId?.toLowerCase()] || cls.teacherId}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                        <div style={{ background: 'var(--bg-primary)', padding: '5px 12px', borderRadius: '8px', color: '#10b981', fontWeight: 'bold', fontSize: '11px', border: '1px solid rgba(255,255,255,0.6)', boxShadow: '2px 2px 5px rgba(163,177,198,0.25), -2px -2px 5px rgba(255,255,255,0.7)' }}>
                                            👥 {attendanceCount} / {cls.students?.length || 0} Joined
                                        </div>
                                        <span style={{ color: 'var(--text-secondary)', fontSize: '13px', transform: isExpanded ? 'rotate(180deg)' : 'none', transition: '0.3s' }}>▼</span>
                                    </div>
                                </div>

                                {/* Expanded class logs */}
                                {isExpanded && (
                                    <div style={{ padding: '24px 28px', background: '#e0e5ec', borderTop: '1px solid rgba(255,255,255,0.45)', boxShadow: 'inset 3px 3px 8px rgba(163,177,198,0.2), inset -3px -3px 8px rgba(255,255,255,0.75)', display: 'flex', flexDirection: 'column', gap: '22px' }}>
                                        
                                        {/* Attendance report */}
                                        <div>
                                            <h4 style={{ color: '#3b82f6', margin: '0 0 14px 0', fontSize: '13px', fontWeight: '850', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📋 Attendance Log & Student Remarks</h4>
                                            {renderAttendance(cls)}
                                        </div>

                                        {cls.coachClassPrivateNotes && (
                                            <>
                                                <div style={{ height: '1px', background: 'rgba(255,255,255,0.45)' }}></div>
                                                {/* Private notes */}
                                                <div style={{ background: '#e0e5ec', border: '1px solid rgba(255, 255, 255, 0.45)', padding: '16px', borderRadius: '12px', boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.25), inset -2px -2px 5px rgba(255,255,255,0.7)' }}>
                                                    <div style={{ color: '#ec4899', fontSize: '12px', fontWeight: '800', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        📓 Class Private Diary (Coaching Remarks)
                                                    </div>
                                                    <div style={{ color: 'var(--text-primary)', fontSize: '13.5px', lineHeight: '1.6', whiteSpace: 'pre-line' }}>{cls.coachClassPrivateNotes}</div>
                                                </div>
                                            </>
                                        )}

                                        {cls.coachStickyNote && (
                                            <>
                                                <div style={{ height: '1px', background: 'rgba(255,255,255,0.45)' }}></div>
                                                {/* Sticky end notes */}
                                                <div style={{ background: '#e0e5ec', border: '1px solid rgba(255, 255, 255, 0.45)', padding: '16px', borderRadius: '12px', boxShadow: 'inset 2px 2px 5px rgba(163,177,198,0.25), inset -2px -2px 5px rgba(255,255,255,0.7)' }}>
                                                    <div style={{ color: '#f59e0b', fontSize: '12px', fontWeight: '800', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        📝 Class Sticky Note (End Summary)
                                                    </div>
                                                    <div style={{ color: 'var(--text-primary)', fontSize: '13.5px', lineHeight: '1.6', whiteSpace: 'pre-line' }}>{cls.coachStickyNote}</div>
                                                </div>
                                            </>
                                        )}

                                        <div style={{ height: '1px', background: 'rgba(255,255,255,0.45)' }}></div>

                                        {/* Collapsible activity timeline */}
                                        <div>
                                            <h4 style={{ color: '#f59e0b', margin: '0 0 14px 0', fontSize: '13px', fontWeight: '850', textTransform: 'uppercase', letterSpacing: '0.5px' }}>⏳ Class Activity Timeline</h4>
                                            {renderTimeline(cls, i)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
